/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useMemo, useCallback } from 'react';
import Styles from '../../css/ServicerSearchLoans.module.css';
import { PatternFormat } from 'react-number-format';
import {
  Select,
  MenuItem,
  createTheme,
  ThemeProvider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableFooter,
} from '@mui/material';
import LoadingModal from '../modals/LoadingModal';
import useLoanSearch from '../../common/hooks/useLoanSearch';
import useGeography from '../../common/hooks/useGeography';
import { useNavigationAlert } from '../../contexts/NavigationAlertContext';
import { useLoanContext } from '../../contexts/LoanContext';
import { isObjEmptyOrNull, isStrEmptyOrNull } from '../../common/UtilityFunctions';
import DeleteIcon from '@mui/icons-material/Delete';
import { useViewingContext } from '../../contexts/ViewingContext';

function getLoanOptions(loans) {
  let optionsSet = new Set();
  let loansSet = new Set<string>();
  for (const item of loans) {
    let brwr = {
      brwrFullName: item.brwrFullName,
      brwrPhone: item.brwrPhone,
      brwrEmail: item.brwrEmail,
      brwrSSNMasked: item.brwrSSNMasked,
    };
    if (loansSet.has(item.loanNo.toString())) {
      optionsSet[item.loanNo].borrowers.push(brwr);
    } else {
      loansSet.add(item.loanNo.toString());
      optionsSet[item.loanNo] = {
        ...item,
        borrowers: [brwr],
      };
    }
  }
  return {
    loans: Array.from(loansSet),
    options: optionsSet,
  };
}

const theme = createTheme({
  typography: {
    fontFamily: `"Poppins", sans-serif`,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: 'normal',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          whiteSpace: 'normal',
        },
        root: {
          width: '100%',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
  },
});

const ServicerSearchLoans = () => {
  const { isServicerView } = useViewingContext();

  const { loanNumber: selectedLoanNumber, setLoanNumber } = useLoanContext();
  const { handleNavigation } = useNavigationAlert();
  const loanSearchInfo = useLoanSearch({
    includeAllBorrowers: true,
    loanNumber: selectedLoanNumber,
  });

  const { loading, errors } = loanSearchInfo.statuses;

  const {
    loanNumber,
    ssn,
    brwrFirstName,
    brwrLastName,
    brwrEmail,
    propAddress,
    propCity,
    propState,
    propZip,
    caseNo,
    phoneNo,
    hazardPolicyNo,
    mersId,
    taxContractNumber,
    bankruptcyCaseNumber,
    investorLoanNo,
    miNo,
    parcelNo,
    includeIntlNo,
    fuzzySearch,
  } = loanSearchInfo.loanSearchInfo;
  const handleLoanSearchParams = loanSearchInfo.handleChange;
  const searchLoans = loanSearchInfo.searchLoans;
  const loans = loanSearchInfo.loans;
  const cachedLoanOptions = useMemo(() => getLoanOptions(loans), [loans]);

  const loansSet = cachedLoanOptions.loans;
  const optionsSet = cachedLoanOptions.options;

  const [checkingLoanOptions, setCheckingLoanOptions] = useState(false);

  const { states } = useGeography();

  const selectLoan = useCallback(
    (loan: string) => {
      setLoanNumber(loan);
      handleNavigation('/loan-information');
    },
    [handleNavigation, setLoanNumber],
  );

  const redirectToLoan = useCallback(
    (res) => {
      if (!isObjEmptyOrNull(res)) {
        if (!isStrEmptyOrNull(ssn) || !isStrEmptyOrNull(loanNumber)) {
          const loansArr = getLoanOptions(res).loans;
          if (loansArr.length === 1) {
            selectLoan(loansArr[0]);
          }
        }
        for (const item of res) {
          if (!isStrEmptyOrNull(loanNumber) && item.loanNo === loanNumber) {
            selectLoan(item.loanNo);
          }
        }
      }
    },
    [ssn, selectLoan, loanNumber],
  );

  const checkLoanOptions = useCallback(
    (res) => {
      if (!isObjEmptyOrNull(res)) {
        redirectToLoan(res);
      }
      setCheckingLoanOptions(false);
    },
    [redirectToLoan],
  );

  const [loansPage, setLoansPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSearch = useCallback(async () => {
    setLoansPage(0);
    setCheckingLoanOptions(true);
    const res = await searchLoans();
    checkLoanOptions(res);
  }, [searchLoans, checkLoanOptions]);

  const handleLoanClick = useCallback(
    (loanNumber: string) => {
      if (loanNumber !== null) {
        setLoanNumber(loanNumber);
        handleNavigation('/loan-information');
      }
    },
    [handleNavigation, setLoanNumber],
  );

  const handleClearAdvanced = useCallback(() => {
    handleLoanSearchParams('edit', 'caseNo', '');
    handleLoanSearchParams('edit', 'investorLoanNo', '');
    handleLoanSearchParams('edit', 'propAddress', '');
    handleLoanSearchParams('edit', 'propZip', '');
    handleLoanSearchParams('edit', 'propCity', '');
    handleLoanSearchParams('edit', 'propState', '');
    handleLoanSearchParams('edit', 'hazardPolicyNo', '');
    handleLoanSearchParams('edit', 'mersId', '');
    handleLoanSearchParams('edit', 'taxContractNumber', '');
    handleLoanSearchParams('edit', 'bankruptcyCaseNumber', '');
    handleLoanSearchParams('edit', 'miNo', '');
    handleLoanSearchParams('edit', 'parcelNo', '');
  }, [handleLoanSearchParams]);

  const handleClearBasic = useCallback(() => {
    handleLoanSearchParams('edit', 'loanNumber', '');
    handleLoanSearchParams('edit', 'brwrFirstName', '');
    handleLoanSearchParams('edit', 'brwrLastName', '');
    handleLoanSearchParams('edit', 'ssn', '');
    handleLoanSearchParams('edit', 'phoneNo', '');
    handleLoanSearchParams('edit', 'brwrEmail', '');
  }, [handleLoanSearchParams]);

  const handleClearSwitches = useCallback(() => {
    handleLoanSearchParams('edit', 'fuzzySearch', false);
    handleLoanSearchParams('edit', 'includeIntlNo', false);
  }, []);

  const handleClear = useCallback(() => {
    handleClearBasic();
    handleClearAdvanced();
    handleClearSwitches();
  }, [handleClearAdvanced, handleClearBasic, handleClearSwitches]);

  function LoanRow(props: { index: number; loan: string }) {
    const { loan } = props;

    return optionsSet[loan].borrowers.map((brwrRow, index) => (
      <TableRow
        key={index}
        className={index === optionsSet[loan].borrowers.length - 1 ? Styles.loanrow : Styles.nobottomborder}
      >
        <TableCell align='left'>
          {index === 0 ? (
            <Button
              className={Styles.loanbutton}
              onClick={() => {
                handleLoanClick(optionsSet[loan].loanNo);
              }}
            >
              {optionsSet[loan].loanNo}
            </Button>
          ) : (
            ''
          )}
        </TableCell>

        <TableCell align='left'> {brwrRow.brwrSSNMasked} </TableCell>

        <TableCell align='left'>{brwrRow.brwrFullName}</TableCell>

        <TableCell align='left'>{optionsSet[loan].servicingStatus}</TableCell>

        <TableCell align='left'>{optionsSet[loan].propFullAddress}</TableCell>
      </TableRow>
    ));
  }

  const selectDropdown = (label: string, options: Array<any>, name, value) => {
    const disabled = options.length === 0;
    return (
      <div className={Styles.boxstyle}>
        <FormControl fullWidth size='small'>
          <InputLabel id='test-select-label'>State</InputLabel>
          <Select
            label='State'
            value={value}
            onChange={(e) => {
              handleLoanSearchParams('edit', name, e.target.value);
            }}
            disabled={disabled}
            className={disabled ? Styles.disabled : ''}
            MenuProps={{ classes: { paper: Styles.menuPaper } }}
            size='small'
          >
            {options.map((item, key) => (
              <MenuItem key={item.code} value={item.code}>
                {item.code + ' - ' + item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  };

  const textInput = (label: string, name, value, func = null) => {
    return (
      <div className={Styles.boxstyle}>
        <TextField
          label={label}
          value={value}
          onChange={(e) => {
            handleLoanSearchParams('edit', name, e.target.value);
            if (func !== null) {
              func(e);
            }
          }}
          size='small'
        />
      </div>
    );
  };

  const patternInput = (label: string, formatStr: string, name, val) => {
    return (
      <div className={Styles.boxstyle}>
        <PatternFormat
          format={formatStr}
          value={val}
          onChange={(e) => {
            handleLoanSearchParams('edit', name, e.target.value);
          }}
          customInput={TextField}
          label={label}
          size='small'
        />
      </div>
    );
  };

  const switchInput = (label: string, name: string, value: boolean, func = null) => {
    return (
      <div className={Styles.switch}>
        <FormControlLabel
          control={
            <Switch
              checked={value}
              onChange={(e) => {
                handleLoanSearchParams('edit', name, e.target.checked);
                if (func !== null) {
                  func(e);
                }
              }}
            />
          }
          label={label}
        />
      </div>
    );
  };

  const clearAllButton = () => {
    return (
      <div className={Styles.clearall}>
        <Button variant='outlined' startIcon={<DeleteIcon />} onClick={handleClear}>
          Clear All
        </Button>
      </div>
    );
  };

  const searchLoansButton = () => {
    const disabled = loading;
    return (
      <div className={Styles.boxstyleButtons}>
        <Button
          className={disabled ? `${Styles.primbutton} ${Styles.disabled}` : Styles.primbutton}
          variant='outlined'
          fullWidth
          onClick={handleSearch}
          size='large'
          disabled={disabled}
        >
          {loading ? 'Loading...' : 'Search Loans'}
        </Button>
      </div>
    );
  };

  const GoToLoanInformationButton = () => {
    const disabled = loading || selectedLoanNumber === '';
    return (
      <div className={Styles.boxstyleButtons}>
        <Button
          className={disabled ? `${Styles.secondarybutton} ${Styles.disabled}` : Styles.secondarybutton}
          variant='outlined'
          fullWidth
          onClick={() => {
            handleNavigation('/loan-information');
          }}
          size='large'
          disabled={disabled}
        >
          {'Selected Loan Information'}
        </Button>
      </div>
    );
  };

  const errorsText = () => {
    return (
      errors !== '' && (
        <TableRow className={Styles.errorrow}>
          <TableCell align='left' colSpan={6} className={Styles.text}>
            {errors}
          </TableCell>
        </TableRow>
      )
    );
  };

  const handleChangePage = useCallback((event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setLoansPage(newPage);
  }, []);

  const loadedLoansTable = () => {
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = Math.max(0, (1 + loansPage) * 10 - loans.length);
    return (
      <div className={Styles.bigtable}>
        <Table size='small' className={Styles.taxTable}>
          <TableHead>
            <TableRow>
              <TableCell align='left' className={Styles.loannumbercell}>
                Loan Number
              </TableCell>
              <TableCell align='left' className={Styles.loannumbercell}>
                SSN
              </TableCell>
              <TableCell align='left' className={Styles.loannumbercell}>
                Borrower Name
              </TableCell>
              <TableCell align='left' className={Styles.statuscell}>
                Status
              </TableCell>
              <TableCell align='left' className={Styles.propaddresscell}>
                Property Address
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errorsText()}

            {loansSet.length !== 0 &&
              loansSet
                .slice(loansPage * rowsPerPage, loansPage * rowsPerPage + rowsPerPage)
                .map((loan, index) => <LoanRow key={loan} index={index} loan={loan} />)}

            {emptyRows > 0 && (
              <TableRow style={{ height: 41 * emptyRows }}>
                <TableCell colSpan={6} aria-hidden />
              </TableRow>
            )}
          </TableBody>

          <TableFooter className={Styles.loanrow}>
            <TableRow>
              <TablePagination
                colSpan={6}
                count={loansSet.length}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
                page={loansPage}
                onPageChange={handleChangePage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    );
  };

  const [advancedSearch, setAdvancedSearch] = useState(false);

  const advancedSwitch = () => {
    return (
      <div className={Styles.switch}>
        <FormControlLabel
          control={
            <Switch
              checked={advancedSearch}
              onChange={(e) => {
                setAdvancedSearch(e.target.checked);
                if (e.target.checked) {
                  setRowsPerPage(20);
                } else {
                  setRowsPerPage(10);
                  handleClearAdvanced();
                }
              }}
            />
          }
          label='Show Advanced Search'
        />
      </div>
    );
  };

  const handlePhoneNoChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value === '') {
      handleLoanSearchParams('edit', 'includeIntlNo', false);
    }
  }, []);

  return isServicerView ? (
    <ThemeProvider theme={theme}>
      <div className={`${Styles.grid}`}>
        {(loading || checkingLoanOptions) && <LoadingModal open={true} />}

        {
          <div className={Styles.smallcolumn}>
            {patternInput('Loan Number', '############', 'loanNumber', loanNumber)}
            {textInput('First Name', 'brwrFirstName', brwrFirstName)}
            {textInput('Last Name', 'brwrLastName', brwrLastName)}
            {patternInput('Social Security Number', '###-##-####', 'ssn', ssn)}
            {textInput('Phone Number', 'phoneNo', phoneNo, handlePhoneNoChange)}
            {!isStrEmptyOrNull(phoneNo) && switchInput('International Phone', 'includeIntlNo', includeIntlNo)}
            {textInput('Email Address', 'brwrEmail', brwrEmail)}
            {clearAllButton()}
            {advancedSwitch()}
            {switchInput('Enable Fuzzy Search (slower)', 'fuzzySearch', fuzzySearch)}
            {advancedSearch && (
              <>
                {textInput('Agency Case Number', 'caseNo', caseNo)}
                {textInput('Investor Loan Number', 'investorLoanNo', investorLoanNo)}
                {textInput('Street Address', 'propAddress', propAddress)}
                {patternInput('Zip Code', '#########', 'propZip', propZip)}
                {textInput('City', 'propCity', propCity)}
                {selectDropdown('State', states, 'propState', propState)}
                {textInput('Hazard Policy No.', 'hazardPolicyNo', hazardPolicyNo)}
                {textInput('Tax (Parcel) No', 'parcelNo', parcelNo)}
                {textInput('MI No', 'miNo', miNo)}
                {textInput('MERS Number', 'mersId', mersId)}
                {textInput('Tax Contract Number', 'taxContractNumber', taxContractNumber)}
                {patternInput('Bankruptcy Case Number', '##-#####', 'bankruptcyCaseNumber', bankruptcyCaseNumber)}
              </>
            )}

            {searchLoansButton()}
            {selectedLoanNumber !== '' && GoToLoanInformationButton()}
          </div>
        }

        {loadedLoansTable()}
      </div>
    </ThemeProvider>
  ) : (
    <div className={Styles.text}>
      Please <a href='/redcarpetservice/login-servicer'>Login</a>
    </div>
  );
};

export default ServicerSearchLoans;
